<template>
  <div>
    <div class="my-5 text-center">
      <h4 class="font-weight-bolder letter-spacing-1px mb-3">
        {{ !twoFactorRequired ? 'Sign in to your account' : 'Two-Factor Authentication'}}
      </h4>
      <div class="small" v-if="!twoFactorRequired">
        Don't have an account? <router-link :to="{ name: 'signup' }">Start your 15-day free trial.</router-link>
      </div>
      <div class="small" v-else>
        Check your authentication app and enter the 6-digit code to verify
      </div>
    </div>
    <form v-if="!twoFactorRequired" id="loginForm" class="my-5" @submit.prevent="submit">
      <google-auth-cta
        mode="login"
        :disabled="loading"
        :redirect-path="$route.query.redirect || '/'"
        @error="onGoogleAuthError"
      />
      <div class="col-form-label text-left text-dark">Work email address</div>
      <b-input id="email" class="input-login" type="email" autocomplete="username" required v-model="credentials.email" autofocus tabindex="1" placeholder="Your work email address" />
      <div class="col-form-label text-left text-dark mt-2">Password</div>
      <b-input id="password" class="input-login" type="password" autocomplete="current-password" required v-model="credentials.password" tabindex="2" placeholder="Your password" />
      <router-link :to="{ path: '/password-reset/' + credentials.email }" class="text-primary small mb-3 mt-1" style="margin-left: 2px;">
        Forgot password?
      </router-link>
      <div class="d-flex justify-content-end align-items-center">
        <ladda-btn :loading="loading" data-style="expand-left" type="submit" class="btn btn-login" tabindex="3" style="min-width: 97px;">Sign In</ladda-btn>
      </div>
    </form>

    <b-alert v-if="!twoFactorRequired" v-model="error.visible" dismissible variant="danger" class="mt-5">{{ error.text }}</b-alert>

    <form v-else id="twoFactorForm" @submit.prevent="verifyTwoFactorLogin">
      <div v-if="showTimerDisplay" class="text-muted small mb-3 text-center">
        Time left: {{ formattedTwoFactorTimer }}
      </div>
      <b-checkbox name="remember2FA" v-model="twoFactorRemember" v-if="!twoFactorWithRecoveryCode" tabindex="2" class="small mb-3" style="line-height: 1.25rem;">Trust this device for 30 days.</b-checkbox>
      <div class="d-flex justify-content-between mb-2">
        <b-input id="twoFactorCode" name="twoFactorCode" v-model="twoFactorCode" :class="{ 'd-none': twoFactorWithRecoveryCode }"
                 class="input-login 2faLogin"
                 type="text" inputmode="numeric" pattern="[0-9]*" minlength="6" maxlength="6" autocomplete="one-time-code" required autofocus placeholder="6-digit code" tabindex="1" />
        <b-input id="twoFactorRecoveryCode" name="twoFactorRecoveryCode" v-model="twoFactorCode" :class="{ 'd-none': !twoFactorWithRecoveryCode }"
                 class="input-login 2faLogin"
                 type="text" inputmode="text" autofocus placeholder="Your recovery code" />
        <ladda-btn :loading="loading" data-style="expand-left" type="submit" class="btn btn-success ml-2">Verify</ladda-btn>
      </div>
      <a href="#" @click="clearTwoFactorTimer(); $api.logout()" class="d-inline-block mt-2 text-uppercase font-weight-bold text-primary small">
        <span class="material-icons md-14 arrow-west" style="vertical-align: text-top;">west</span>
        Back to login
      </a>
      <b-alert v-model="twoFactorError.visible" dismissible variant="danger" class="mt-5">
        {{ twoFactorError.text }}
      </b-alert>
      <div class="text-muted small text-center mt-5">
        Lost your device with an authenticator? Use your <a href="javascript:void(0)" @click="useRecoveryCode">recovery codes</a> or contact <a href="mailto:cs@exatom.io?subject=2FA%20reset">Customer Success.</a>
      </div>
    </form>

  </div>
</template>

<script>
import LaddaBtn from '@/vendor/libs/ladda/Ladda'
import router from '@/router'
import { generateUUIDv4 } from '@/utils'
import GoogleAuthCta from '@/components/auth/GoogleAuthCta.vue'
import { isGoogleProviderAuthError } from '@/utils/googleAuth'

export default {
  name: 'login',
  metaInfo: {
    title: 'Exatom - Login to your account'
  },
  components: { LaddaBtn, GoogleAuthCta },
  data () {
    return {
      credentials: {
        email: (typeof this.$route.params.email !== 'undefined' ? this.$route.params.email : ''),
        password: ''
      },
      error: {
        text: (typeof this.$route.query.error !== 'undefined' ? this.$route.query.error : ''),
        visible: typeof this.$route.query.error !== 'undefined'
      },
      loading: false,
      twoFactorRequired: false,
      twoFactorCode: '',
      twoFactorRemember: false,
      twoFactorError: { text: '', visible: false },
      twoFactorTimer: 0,
      twoFactorInterval: null,
      twoFactorWithRecoveryCode: false,
    }
  },
  async mounted () {
    if (typeof this.$route.params.email !== 'undefined') {
      // Change URL to not hold PII data
      history.replaceState({}, '', document.location.origin + '/login')
    }

    if (typeof this.$route.query.token !== 'undefined') {
      // Change URL to not hold PII data
      history.replaceState({}, '', document.location.origin + '/login')

      if (this.$route.query.token.length > 512) {
        localStorage.apiToken = this.$route.query.token
        await this.$root.$api.refresh()
      }
    }

    // Is the user already logged in?
    if (!this.$api.isLoginTokenExpired()) {
      await router.push({ path: '/' })
    } else {
      // Make sure if there is a previous selected client it's cleared
      this.$root.client = null
    }
  },
  beforeDestroy () {
    this.clearTwoFactorTimer()
  },
  computed: {
    showTimerDisplay () {
      return this.twoFactorTimer <= 240
    },
    formattedTwoFactorTimer () {
      const m = Math.floor(this.twoFactorTimer / 60)
      const s = this.twoFactorTimer % 60
      return `${m}:${s < 10 ? '0' + s : s}`
    }
  },
  methods: {
    onGoogleAuthError () {
      this.error = {
        text: this.$root.i18n.t('google_auth_error_start_login'),
        visible: true
      }
    },
    async submit () {
      this.error.visible = false
      this.loading = true

      try {
        await this.$api.login(this.credentials, localStorage?.twoFactorRememberToken)

        // When logging-in again we reset the previously held insights custom timeframe
        // -> sessionStorage = per tab
        // -> localStorage = browser (across tabs)
        window.sessionStorage.removeItem('insights_custom_from')
        window.sessionStorage.removeItem('insights_custom_to')

        this.loading = false

        // If two-factor is enabled and not yet verified, prompt for code
        try {
          const token = localStorage.apiToken
          const payload = JSON.parse(atob(token.split('.')[1]))
          if (payload?.user?.two_factor && payload?.user?.two_factor.enabled && !payload?.user?.two_factor.verified) {
            this.twoFactorRequired = true

            const numericOnly = (e) => {
              if (e instanceof KeyboardEvent) {
                if (e.key === ' ') e.preventDefault()
                if (e.key.length > 1) return
                if (!/^[0-9+]$/.test(e.key) && !e.ctrlKey && !e.altKey && !e.metaKey) e.preventDefault()
              }
            }
            setTimeout(() => {
              document.getElementById("twoFactorCode").focus();
              document.getElementById("twoFactorCode").addEventListener('keydown', numericOnly, { capture: true })
            }, 100)

            this.startTwoFactorTimer()
            return
          }
        } catch (e) {}

        if (this.$route.query.redirect) {
          window.location.href = this.$route.query.redirect
        } else {
          this.$router.push({ path: '/' })
        }
      } catch (error) {
        this.loading = false
        if (error.response && error.response.status === 401) {
          if (isGoogleProviderAuthError(error)) {
            this.error = {
              text: error.response.data.message,
              visible: true
            }
            return
          }
          this.error = {
            text: 'Invalid email and password combination.',
            visible: true
          }
        } else {
          this.error = {
            text: 'A technical error occurred, please try again in a few minutes.',
            visible: true
          }
        }
      }
    },

    startTwoFactorTimer () {
      this.twoFactorTimer = 300
      this.twoFactorInterval = setInterval(() => {
        if (this.twoFactorTimer > 0) {
          this.twoFactorTimer--
        } else {
          this.clearTwoFactorTimer()
          this.$api.logout()
        }
      }, 1000)
    },

    clearTwoFactorTimer () {
      if (this.twoFactorInterval) {
        clearInterval(this.twoFactorInterval)
        this.twoFactorInterval = null
      }
    },

    useRecoveryCode () {
      document.getElementById("twoFactorCode").required = false;
      document.getElementById("twoFactorCode").removeAttribute('pattern');
      this.twoFactorWithRecoveryCode = true
      setTimeout(() => {
        document.getElementById("twoFactorRecoveryCode").focus();
        document.getElementById("twoFactorRecoveryCode").required = true;
      }, 100);
    },

    async verifyTwoFactorLogin () {
      // If you're using a recovery code, do not remember
      if (this.twoFactorWithRecoveryCode) {
        this.twoFactorRemember = false
      }
      if (!this.twoFactorRemember) {
        try {
          delete localStorage.twoFactorRememberToken
        } catch (e) {
          console.error('Unable to delete localStorage.twoFactorRememberToken: ', e)
        }
      } else {
        localStorage.twoFactorRememberToken = generateUUIDv4()
      }

      this.twoFactorError.visible = false
      this.loading = true
      try {
        await this.$api.twoFactorVerify(this.twoFactorCode, localStorage?.twoFactorRememberToken)
        this.clearTwoFactorTimer()
        this.loading = false
        if (this.$route.query.redirect) {
          window.location.href = this.$route.query.redirect
        } else {
          this.$router.push({ path: '/' })
        }
      } catch (error) {
        this.loading = false
        if (error.response) {
          this.twoFactorError = { text: error.response.data.message, visible: true }
        }
      }
    }
  },
  watch: {
    twoFactorCode () {
      if (this.twoFactorCode.length === 6) {
        this.verifyTwoFactorLogin()
      }
    }
  }
}
</script>
